import { useEffect, useMemo, useRef, useState } from 'react';
import type { IconSvgElement } from '@hugeicons/react';
import {
  Cancel01Icon,
  FileDownloadIcon,
  FileUploadIcon,
  FilterHorizontalIcon,
  FloppyDiskIcon,
  MailSend02Icon,
  RefreshIcon,
  Search01Icon,
  UserAdd01Icon,
} from '@hugeicons/core-free-icons';
import { Button } from '../../components/ui/Button';
import { CustomSelect } from '../../components/ui/CustomSelect';
import { HugeIcon } from '../../components/ui/HugeIcon';
import { Input } from '../../components/ui/Input';
import { StatusPill } from '../../components/ui/StatusPill';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { SeatUsageCard } from '../../components/layout/SeatUsageCard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { OperationalTableCard } from '../../components/ui/operational-table-card';
import {
  requestUserAccess,
  refreshOrganizationUsers,
  cancelUserRequest,
  resendMemberInvitation,
  revokeMemberInvitation,
  roleSchema,
  updateMemberAccess,
  type ConsoleState,
  type Member,
  type UserRequest,
} from '../../lib/api';
import { Pagination, PaginationControls, PaginationNext, PaginationPrevious, PaginationStatus } from '../../components/ui/pagination';
import { downloadMembersCsv, UserImportDialog } from './UserImportDialog';

type RoleFilter = 'all' | 'org_admin' | 'member';
type StatusFilter = 'all' | Member['status'];
type AddRole = 'org_admin' | 'member';
type MemberField = 'role' | 'status';
type MemberDraft = { role: Member['role']; status: Member['status'] };

const roleOptions: Array<{ label: string; value: Member['role'] }> = [
  { label: 'Admin', value: 'org_admin' },
  { label: 'Member', value: 'member' },
];

const statusOptions: Array<{ label: string; value: Member['status'] }> = [
  { label: 'Active', value: 'active' },
  { label: 'Pending activation', value: 'pending_activation' },
  { label: 'Revoked', value: 'revoked' },
];

const roleFilterOptions: Array<{ label: string; value: RoleFilter }> = [
  { label: 'All Roles', value: 'all' },
  { label: 'Admin', value: 'org_admin' },
  { label: 'Member', value: 'member' },
];

const statusFilterOptions: Array<{ label: string; value: StatusFilter }> = [
  { label: 'All Statuses', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Pending Activation', value: 'pending_activation' },
  { label: 'Revoked', value: 'revoked' },
];

export function UsersPage({
  license,
  members,
  userRequests,
  onStateChange,
}: {
  license: ConsoleState['license'];
  members: Member[];
  userRequests: UserRequest[];
  onStateChange: React.Dispatch<React.SetStateAction<ConsoleState | null>>;
}) {
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AddRole>('member');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [memberDraft, setMemberDraft] = useState<MemberDraft | null>(null);
  const [pendingChange, setPendingChange] = useState<{
    member: Member;
    draft: MemberDraft;
  } | null>(null);
  const [memberUpdateError, setMemberUpdateError] = useState<string | null>(null);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);
  const [pendingReminderMember, setPendingReminderMember] = useState<Member | null>(null);
  const [resendingMemberId, setResendingMemberId] = useState<string | null>(null);
  const [reminderError, setReminderError] = useState<string | null>(null);
  const [reminderNotice, setReminderNotice] = useState<string | null>(null);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [cancellingRequestId, setCancellingRequestId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pendingRevokeMember, setPendingRevokeMember] = useState<Member | null>(null);
  const [revokingMemberId, setRevokingMemberId] = useState<string | null>(null);
  const [isRefreshingUsers, setIsRefreshingUsers] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  const usedSeats = members.filter((member) => member.status !== 'revoked').length;
  const seatLimit = license?.seatLimit ?? 0;
  const remainingSeats = Math.max(0, seatLimit - usedSeats);

  const directoryMembers = useMemo(
    () => members.filter(
      (member) => !(member.status === 'revoked' && member.observedStatus === 'missing')
    ),
    [members]
  );
  const filteredMembers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return directoryMembers.filter((member) => {
      const matchesQuery =
        !normalizedQuery ||
        member.fullName.toLowerCase().includes(normalizedQuery) ||
        member.email.toLowerCase().includes(normalizedQuery);
      const matchesRole = roleFilter === 'all' || member.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || member.status === statusFilter;

      return matchesQuery && matchesRole && matchesStatus;
    });
  }, [directoryMembers, query, roleFilter, statusFilter]);
  const pageCount = Math.max(1, Math.ceil(filteredMembers.length / 15));
  const pagedMembers = filteredMembers.slice((page - 1) * 15, page * 15);

  useEffect(() => { setPage(1); }, [query, roleFilter, statusFilter]);
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);

  useEffect(() => {
    if (!filterOpen) return;

    function closeFilterOnOutsidePointer(event: PointerEvent) {
      if (!filterRef.current?.contains(event.target as Node)) {
        setFilterOpen(false);
      }
    }

    document.addEventListener('pointerdown', closeFilterOnOutsidePointer);

    return () => {
      document.removeEventListener('pointerdown', closeFilterOnOutsidePointer);
    };
  }, [filterOpen]);

  useEffect(() => {
    const interval = window.setInterval(() => setClockNow(Date.now()), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  async function submitUser(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      const result = await requestUserAccess({ email, fullName, role });
      onStateChange((current) => {
        if (!current) return current;

        if (result.member) {
          const existingIndex = current.members.findIndex(
            (member) => member.id === result.member?.id
          );
          if (existingIndex === -1) {
            return { ...current, members: [result.member, ...current.members] };
          }

          return {
            ...current,
            members: current.members.map((member) =>
              member.id === result.member?.id ? result.member : member
            ),
          };
        }

        if (result.request) {
          return { ...current, userRequests: [result.request, ...current.userRequests] };
        }

        return current;
      });

      if (result.outcome === 'approval_required') {
        setMessage('No seats are available. This request was sent to myPDF for approval.');
      } else {
        setMessage('User added as pending activation. An invitation email has been queued.');
        setFullName('');
        setEmail('');
        setRole('member');
      }
      setDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to add user.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function refreshUsers() {
    setIsRefreshingUsers(true);
    setRefreshError(null);
    setReminderNotice(null);
    try {
      const result = await refreshOrganizationUsers();
      onStateChange((current) => current ? { ...current, members: result.members } : current);
      const changes = result.imported + result.revoked;
      setReminderNotice(
        changes
          ? `Users refreshed. ${result.imported} added and ${result.revoked} removed from active access.`
          : `Users refreshed from managed identity at ${formatExactDate(result.syncedAt)}.`
      );
    } catch (err) {
      setRefreshError(err instanceof Error ? err.message : 'Unable to refresh organization users.');
    } finally {
      setIsRefreshingUsers(false);
    }
  }

  function startEditingMember(member: Member) {
    setMemberUpdateError(null);
    setEditingMemberId(member.id);
    setMemberDraft({ role: member.role, status: member.status });
  }

  function cancelEditingMember() {
    setEditingMemberId(null);
    setMemberDraft(null);
    setMemberUpdateError(null);
  }

  function updateMemberDraft(field: MemberField, value: Member['role'] | Member['status']) {
    setMemberDraft((current) => (current ? { ...current, [field]: value } : current));
  }

  const editingMember = members.find((member) => member.id === editingMemberId) ?? null;
  const hasDraftChanges = Boolean(
    editingMember &&
      memberDraft &&
      (editingMember.role !== memberDraft.role || editingMember.status !== memberDraft.status)
  );

  function requestMemberSave() {
    if (!editingMember || !memberDraft || !hasDraftChanges) return;
    setMemberUpdateError(null);
    setPendingChange({ member: editingMember, draft: memberDraft });
  }

  async function confirmMemberChange() {
    if (!pendingChange) return;

    setIsUpdatingMember(true);
    setMemberUpdateError(null);

    try {
      const update: { role?: Member['role']; status?: Member['status'] } = {};
      if (pendingChange.member.role !== pendingChange.draft.role) update.role = pendingChange.draft.role;
      if (pendingChange.member.status !== pendingChange.draft.status) update.status = pendingChange.draft.status;
      const updatedMember = await updateMemberAccess(pendingChange.member.id, update);
      onStateChange((current) => {
        if (!current) return current;

        return {
          ...current,
          currentUser:
            current.currentUser.id === updatedMember.id ? updatedMember : current.currentUser,
          members: current.members.map((member) =>
            member.id === updatedMember.id ? updatedMember : member
          ),
        };
      });
      setPendingChange(null);
      setEditingMemberId(null);
      setMemberDraft(null);
    } catch (err) {
      setMemberUpdateError(err instanceof Error ? err.message : 'Unable to update user.');
    } finally {
      setIsUpdatingMember(false);
    }
  }

  async function confirmInvitationReminder() {
    if (!pendingReminderMember) return;

    setResendingMemberId(pendingReminderMember.id);
    setReminderError(null);
    setReminderNotice(null);

    try {
      const result = await resendMemberInvitation(pendingReminderMember.id);
      onStateChange((current) => {
        if (!current) return current;
        return {
          ...current,
          members: current.members.map((member) =>
            member.id === result.memberId
              ? {
                  ...member,
                  invitationLastSentAt: result.invitationLastSentAt,
                  invitationCanResendAt: result.invitationCanResendAt,
                }
              : member
          ),
        };
      });
      setReminderNotice(`Invitation resent to ${pendingReminderMember.email}.`);
      setPendingReminderMember(null);
      setClockNow(Date.now());
    } catch (err) {
      const canResendAt =
        err instanceof Error &&
        'canResendAt' in err &&
        typeof err.canResendAt === 'string'
          ? err.canResendAt
          : null;
      if (canResendAt) {
        onStateChange((current) =>
          current
            ? {
                ...current,
                members: current.members.map((member) =>
                  member.id === pendingReminderMember.id
                    ? { ...member, invitationCanResendAt: canResendAt }
                    : member
                ),
              }
            : current
        );
      }
      setReminderError(err instanceof Error ? err.message : 'Unable to resend invitation.');
    } finally {
      setResendingMemberId(null);
    }
  }

  async function confirmInvitationRevoke() {
    if (!pendingRevokeMember) return;
    setRevokingMemberId(pendingRevokeMember.id);
    setReminderError(null);
    try {
      const updated = await revokeMemberInvitation(pendingRevokeMember.id);
      onStateChange((current) => current ? {
        ...current,
        members: current.members.map((member) => member.id === updated.id ? updated : member),
      } : current);
      setReminderNotice(`Invitation revoked for ${pendingRevokeMember.email}.`);
      setPendingRevokeMember(null);
    } catch (err) {
      setReminderError(err instanceof Error ? err.message : 'Unable to revoke invitation.');
    } finally {
      setRevokingMemberId(null);
    }
  }

  function closeDialog() {
    setDialogOpen(false);
    setError(null);
    setMessage(null);
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <SeatUsageCard limit={seatLimit} used={usedSeats} />
        <SummaryCard detail={userRequests.length ? 'Awaiting management review' : 'No approvals are waiting'} icon={MailSend02Icon} iconTone="bg-amber-500/14 text-amber-700" label="Pending approvals" value={String(userRequests.length)} />
      </div>

      <OperationalTableCard
        action={(
          <div className="flex w-full min-w-0 flex-nowrap items-center gap-2 sm:w-auto">
            <div className="relative min-w-0 flex-1 sm:w-56 sm:flex-none lg:w-72">
              <HugeIcon
                className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-slate-500"
                icon={Search01Icon}
                size={16}
              />
              <Input
                aria-label="Search organization users"
                className="w-full pl-9"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search users"
                value={query}
              />
            </div>
            <div className="relative flex items-center" ref={filterRef}>
              <Button
                aria-expanded={filterOpen}
                aria-label="Filter users"
                className="shrink-0"
                size="icon"
                onClick={() => setFilterOpen((current) => !current)}
                type="button"
                variant="outline"
              >
                <HugeIcon icon={FilterHorizontalIcon} size={18} />
              </Button>

              {filterOpen ? (
                <div className="absolute right-0 top-[calc(100%+6px)] z-20 w-[232px] rounded-2xl bg-popover p-2 text-popover-foreground shadow-lg ring-1 ring-foreground/5">
                  <FilterGroup label="Roles">
                    {roleFilterOptions.map((option) => (
                      <FilterOption
                        key={option.value}
                        label={option.label}
                        onSelect={() => setRoleFilter(option.value)}
                        selected={roleFilter === option.value}
                      />
                    ))}
                  </FilterGroup>

                  <FilterGroup label="Status">
                    {statusFilterOptions.map((option) => (
                      <FilterOption
                        key={option.value}
                        label={option.label}
                        onSelect={() => setStatusFilter(option.value)}
                        selected={statusFilter === option.value}
                      />
                    ))}
                  </FilterGroup>
                </div>
              ) : null}
            </div>
            <Button
              aria-label="Refresh organization users"
              disabled={isRefreshingUsers}
              onClick={() => { void refreshUsers(); }}
              size="icon"
              title="Refresh users"
              type="button"
              variant="outline"
            >
              <HugeIcon className={isRefreshingUsers ? 'animate-spin' : undefined} icon={RefreshIcon} size={18} />
            </Button>
            <Button aria-label="Import users from CSV" onClick={() => setImportOpen(true)} size="icon" title="Import users" type="button" variant="outline">
              <HugeIcon icon={FileUploadIcon} size={18} />
            </Button>
            <Button aria-label="Export organization users to CSV" onClick={() => downloadMembersCsv(filteredMembers)} size="icon" title="Export users" type="button" variant="outline">
              <HugeIcon icon={FileDownloadIcon} size={18} />
            </Button>
            <Button
              aria-label="Save user changes"
              className={hasDraftChanges ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'disabled:bg-slate-200 disabled:text-slate-500'}
              disabled={!hasDraftChanges || isUpdatingMember}
              onClick={requestMemberSave}
              size="icon"
              title="Save changes"
              type="button"
            >
              <HugeIcon icon={FloppyDiskIcon} size={18} />
            </Button>
            <Button aria-label="Add user" onClick={() => setDialogOpen(true)} size="icon" title="Add user" type="button">
              <HugeIcon icon={UserAdd01Icon} size={18} />
            </Button>
          </div>
        )}
        className="relative"
        information={`${filteredMembers.length} of ${directoryMembers.length} users shown. ${directoryMembers.length} total organization ${directoryMembers.length === 1 ? 'user' : 'users'}.`}
        informationLabel="Organization users information"
        notice={refreshError ? (
          <div className="border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">
            {refreshError}
          </div>
        ) : reminderNotice ? (
          <div className="border-b border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
            {reminderNotice}
          </div>
        ) : null}
        title="Organization users"
      >
        <Table className="min-w-[920px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-4">Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last sign-in</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="pr-4 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedMembers.length ? pagedMembers.map((member) => {
              const isEditing = editingMemberId === member.id;
              const currentDraft = isEditing && memberDraft ? memberDraft : { role: member.role, status: member.status };
              const reminderAvailableAt = member.invitationCanResendAt ? Date.parse(member.invitationCanResendAt) : Number.NaN;
              const reminderCoolingDown = member.status === 'pending_activation' && Number.isFinite(reminderAvailableAt) && reminderAvailableAt > clockNow;

              return (
                <TableRow key={member.id}>
                  <TableCell className="pl-4 font-semibold text-foreground">{member.fullName}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{member.email}</TableCell>
                  <TableCell>
                    {isEditing ? <MemberSelection field="role" onSelect={(value) => updateMemberDraft('role', value)} options={roleOptions} value={currentDraft.role} /> : formatRole(member.role)}
                  </TableCell>
                  <TableCell>
                    {isEditing ? <MemberSelection field="status" onSelect={(value) => updateMemberDraft('status', value)} options={statusOptions} value={currentDraft.status} /> : <div className="flex flex-col items-start gap-1"><StatusPill status={member.status} />{member.status === 'pending_activation' ? <p className="text-[11px] text-muted-foreground">{member.invitationExpiresAt ? `Expires ${formatDate(member.invitationExpiresAt)}` : member.invitationLastSentAt ? `Sent ${formatDate(member.invitationLastSentAt)}` : 'Invitation queued'}</p> : null}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{member.lastLoginAt ? formatExactDate(member.lastLoginAt) : 'No sign-in'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(member.createdAt)}</TableCell>
                  <TableCell className="pr-4">
                    <div className="flex items-center justify-end gap-2">
                      {isEditing ? (
                        <Button onClick={cancelEditingMember} size="sm" type="button" variant="outline"><HugeIcon icon={Cancel01Icon} size={14} />Cancel</Button>
                      ) : (
                        <>
                          {member.status === 'pending_activation' ? (
                            <><Button
                              aria-label={reminderCoolingDown ? `Invitation reminder available ${formatExactDate(member.invitationCanResendAt)}` : `Resend invitation to ${member.email}`}
                              className="bg-muted text-xs hover:bg-muted/80"
                              disabled={Boolean(editingMemberId) || resendingMemberId === member.id || reminderCoolingDown}
                              onClick={() => { setReminderError(null); setReminderNotice(null); setPendingReminderMember(member); }}
                              title={reminderCoolingDown ? `Available ${formatExactDate(member.invitationCanResendAt)}` : `Resend invitation to ${member.email}`}
                              type="button"
                              variant="ghost"
                            >
                              <HugeIcon icon={MailSend02Icon} size={14} />
                              {reminderCoolingDown ? formatCooldown(reminderAvailableAt - clockNow) : 'Resend invite'}
                            </Button><Button className="bg-destructive/12 text-xs text-destructive hover:bg-destructive/20 hover:text-destructive" disabled={Boolean(editingMemberId) || Boolean(revokingMemberId)} onClick={() => { setReminderError(null); setPendingRevokeMember(member); }} size="sm" type="button" variant="ghost">Revoke</Button></>
                          ) : null}
                          <Button className="text-xs" disabled={Boolean(editingMemberId) || Boolean(resendingMemberId)} onClick={() => startEditingMember(member)} size="sm" type="button" variant="outline">Edit</Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            }) : (
              <TableRow><TableCell className="h-24 text-center text-muted-foreground" colSpan={7}>No users match the current filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        {filteredMembers.length > 15 ? <Pagination className="border-t px-4 py-3"><PaginationStatus>Page {page} of {pageCount} · {filteredMembers.length} users</PaginationStatus><PaginationControls><PaginationPrevious disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))} /><PaginationNext disabled={page === pageCount} onClick={() => setPage((current) => Math.min(pageCount, current + 1))} /></PaginationControls></Pagination> : null}
      </OperationalTableCard>

      {userRequests.length ? (
        <section>
          <OperationalTableCard
            information={`${userRequests.length} ${userRequests.length === 1 ? 'request needs' : 'requests need'} attention.`}
            informationLabel="Pending approval requests information"
            title="Pending approval requests"
          >
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-4">Requester</TableHead>
                  <TableHead>Requested role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead className="pr-4 text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="pl-4"><p className="font-semibold">{request.fullName}</p><p className="text-xs text-muted-foreground">{request.email}</p></TableCell>
                    <TableCell>{formatRole(request.requestedRole)}</TableCell>
                    <TableCell><StatusPill status={request.status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(request.createdAt)}</TableCell>
                    <TableCell className="pr-4 text-right">
                      <Button
                        className="text-xs"
                        disabled={cancellingRequestId === request.id}
                        onClick={() => { void (async () => {
                          setCancellingRequestId(request.id);
                          try {
                            await cancelUserRequest(request.id);
                            onStateChange((current) => current ? { ...current, userRequests: current.userRequests.filter((item) => item.id !== request.id) } : current);
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Unable to withdraw request.');
                          } finally {
                            setCancellingRequestId(null);
                          }
                        })(); }}
                        size="sm"
                        type="button"
                        variant="destructive"
                      ><HugeIcon icon={Cancel01Icon} size={14} />Withdraw</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </OperationalTableCard>
        </section>
      ) : null}

      <Dialog onOpenChange={(open) => { if (!open && !isSubmitting) closeDialog(); }} open={dialogOpen}>
        <DialogContent>
          <form className="flex flex-col gap-4" onSubmit={(event) => { void submitUser(event); }}>
            <DialogHeader>
              <DialogTitle>Add user</DialogTitle>
              <DialogDescription>{remainingSeats > 0 ? `${remainingSeats} seats available` : 'No seats available. Approval is required.'}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <Input
                autoComplete="name"
                onChange={(event) => setFullName(event.target.value)}
                placeholder="Full name"
                required
                value={fullName}
              />
              <Input
                autoComplete="email"
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Email address"
                required
                type="email"
                value={email}
              />
              <CustomSelect
                ariaLabel="New user role"
                className="border-border bg-white"
                fullWidth
                label="New user role"
                onChange={(value) => setRole(roleSchema.parse(value))}
                options={[
                  { value: 'member', label: 'Member' },
                  { value: 'org_admin', label: 'Organization admin' },
                ]}
                value={role}
              />
              {message ? <p className="text-xs font-medium leading-5 text-emerald-700">{message}</p> : null}
              {error ? <p className="text-xs font-medium leading-5 text-rose-700">{error}</p> : null}
            </div>
            <DialogFooter>
              <Button onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="submit">
                {isSubmitting
                  ? 'Submitting'
                  : remainingSeats > 0
                    ? 'Add user'
                    : 'Request approval'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <UserImportDialog
        onClose={() => setImportOpen(false)}
        onImported={(imported) => {
          onStateChange((current) => current ? { ...current, members: [...imported, ...current.members] } : current);
          setReminderNotice(`${imported.length} ${imported.length === 1 ? 'user was' : 'users were'} imported and invitations were queued.`);
          setImportOpen(false);
        }}
        open={importOpen}
      />

      {pendingChange ? (
        <ConfirmMemberChangeDialog
          change={pendingChange}
          error={memberUpdateError}
          isSubmitting={isUpdatingMember}
          onCancel={() => {
            if (!isUpdatingMember) {
              setPendingChange(null);
              setMemberUpdateError(null);
            }
          }}
          onConfirm={() => {
            void confirmMemberChange();
          }}
        />
      ) : null}

      {pendingReminderMember ? (
        <ConfirmInvitationReminderDialog
          error={reminderError}
          isSubmitting={resendingMemberId === pendingReminderMember.id}
          member={pendingReminderMember}
          onCancel={() => {
            if (!resendingMemberId) {
              setPendingReminderMember(null);
              setReminderError(null);
            }
          }}
          onConfirm={() => {
            void confirmInvitationReminder();
          }}
        />
      ) : null}

      {pendingRevokeMember ? <Dialog onOpenChange={(open) => { if (!open && !revokingMemberId) setPendingRevokeMember(null); }} open><DialogContent><DialogHeader><DialogTitle>Revoke invitation</DialogTitle><DialogDescription>This revokes {pendingRevokeMember.fullName}'s pending access and disables the current invitation. Reactivation will require a new invitation.</DialogDescription></DialogHeader>{reminderError ? <p className="text-xs font-medium text-destructive">{reminderError}</p> : null}<DialogFooter><Button disabled={Boolean(revokingMemberId)} onClick={() => setPendingRevokeMember(null)} variant="outline">Cancel</Button><Button disabled={Boolean(revokingMemberId)} onClick={() => { void confirmInvitationRevoke(); }} variant="destructive">{revokingMemberId ? 'Revoking' : 'Revoke invitation'}</Button></DialogFooter></DialogContent></Dialog> : null}
    </section>
  );
}

function ConfirmInvitationReminderDialog({
  error,
  isSubmitting,
  member,
  onCancel,
  onConfirm,
}: {
  error: string | null;
  isSubmitting: boolean;
  member: Member;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog onOpenChange={(open) => { if (!open && !isSubmitting) onCancel(); }} open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resend invitation</DialogTitle>
          <DialogDescription>Send another invitation email to {member.fullName} at {member.email}?</DialogDescription>
        </DialogHeader>
        {error ? (
          <p className="text-xs font-medium text-destructive">{error}</p>
        ) : null}
        <DialogFooter>
          <Button
            disabled={isSubmitting}
            onClick={onCancel}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            disabled={isSubmitting}
            onClick={onConfirm}
            type="button"
          >
            <HugeIcon icon={MailSend02Icon} size={16} />
            {isSubmitting ? 'Sending' : 'Resend invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatCooldown(milliseconds: number) {
  const minutes = Math.max(1, Math.ceil(milliseconds / 60_000));
  if (minutes < 60) return `Resend in ${minutes}m`;
  return `Resend in ${Math.ceil(minutes / 60)}h`;
}

function formatExactDate(value: string | null) {
  if (!value) return 'when the cooldown ends';
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return 'when the cooldown ends';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(time));
}

function SummaryCard({
  detail,
  icon,
  iconTone,
  label,
  value,
}: {
  detail: string;
  icon: IconSvgElement;
  iconTone: string;
  label: string;
  value: string;
}) {
  return (
    <Card className="gap-3 py-3" size="sm">
      <CardContent className="flex h-full flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className={`flex size-7 items-center justify-center rounded-lg ${iconTone}`}><HugeIcon icon={icon} size={15} strokeWidth={1.9} /></span>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        </div>
        <p className="text-2xl font-semibold tracking-tight">{value}</p>
        <p className="mt-auto text-xs text-muted-foreground">{detail}</p>
      </CardContent>
    </Card>
  );
}

function FilterGroup({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div className="space-y-1.5">
      <p className="px-2 text-xs font-semibold leading-5 text-slate-900">{label}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function FilterOption({
  label,
  onSelect,
  selected,
}: {
  label: string;
  onSelect: () => void;
  selected: boolean;
}) {
  return (
    <button
      aria-pressed={selected}
      className={`flex h-9 w-full items-center gap-2 rounded-[14px] px-2 text-left text-sm font-medium leading-5 transition ${
        selected
          ? 'bg-slate-100 text-slate-900'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
      }`}
      onClick={onSelect}
      type="button"
    >
      {selected ? (
        <img alt="" className="h-[18px] w-[18px] shrink-0" src="/filter-check-selected.svg" />
      ) : (
        <span className="h-[18px] w-[18px] shrink-0 rounded-full border border-slate-200/80 bg-slate-100/70" />
      )}
      {label}
    </button>
  );
}

function MemberSelection<T extends Member['role'] | Member['status']>({
  field,
  onSelect,
  options,
  value,
}: {
  field: MemberField;
  onSelect: (value: T) => void;
  options: Array<{ label: string; value: T }>;
  value: T;
}) {
  return (
    <div className="relative w-full">
      <CustomSelect
        ariaLabel={`Change ${field}`}
        fullWidth
        onChange={onSelect}
        options={options}
        value={value}
        variant="compact"
      />
    </div>
  );
}

function ConfirmMemberChangeDialog({
  change,
  error,
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  change: { member: Member; draft: MemberDraft };
  error: string | null;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog onOpenChange={(open) => { if (!open && !isSubmitting) onCancel(); }} open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm user update</DialogTitle>
          <DialogDescription>
            This will update {change.member.fullName}'s role to {formatRole(change.draft.role)} and status to {formatStatus(change.draft.status)}.
          </DialogDescription>
        </DialogHeader>

        {error ? <p className="text-xs font-medium text-destructive">{error}</p> : null}

        <DialogFooter>
          <Button disabled={isSubmitting} onClick={onCancel} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={isSubmitting} onClick={onConfirm} type="button">
            {isSubmitting ? 'Updating' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatRole(role: Member['role']) {
  return role === 'org_admin' ? 'Admin' : 'Member';
}

function formatStatus(status: Member['status']) {
  if (status === 'pending_activation') return 'Pending activation';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
}
